import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "./NtText";
import { useTheme } from "../context/ThemeContext";

/**
 * Bordered card that wraps a list/table. Header is multi-slot so every list
 * screen composes consistently:
 *
 *   <NtPageSection
 *     title="Extensions"
 *     filters={<NtTabController ... />}
 *     search={<NtSearchBar ... />}
 *     trailing={<IconButton ... />}
 *     footer={<NtPagination ... />}
 *   >
 *     {columns header + rows}
 *   </NtPageSection>
 *
 * Backward-compat: the older `right` prop is still accepted and maps to
 * `trailing` so existing callers don't break.
 */
function NtPageSection({
  title,
  filters,
  search,
  trailing,
  right, // legacy alias for `trailing`
  children,
  footer,
  style,
  headerStyle,
  bodyStyle,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const rightSlot = trailing || right;
  const hasHeader = title || filters || search || rightSlot;

  return (
    <View style={[styles.wrap, style]}>
      {hasHeader ? (
        <View style={[styles.header, headerStyle]}>
          {title ? <NtText style={styles.title}>{title}</NtText> : null}
          {filters ? <View style={styles.filtersSlot}>{filters}</View> : null}
          <View style={{ flex: 1 }} />
          {search ? <View style={styles.searchSlot}>{search}</View> : null}
          {rightSlot ? <View style={styles.trailing}>{rightSlot}</View> : null}
        </View>
      ) : null}
      <View style={[styles.body, bodyStyle]}>{children}</View>
      {footer || null}
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    wrap: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      overflow: "hidden",
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    title: {
      fontSize: 12,
      fontWeight: "600",
      color: t.text,
      marginRight: 8,
    },
    filtersSlot: { flexDirection: "row", alignItems: "center", gap: 4 },
    searchSlot: { minWidth: 200, maxWidth: 280 },
    trailing: { flexDirection: "row", alignItems: "center", gap: 6 },
    body: {},
  });

export default NtPageSection;
